import React, { useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 10;

function paginate(arr, page) {
  const start = (page - 1) * PAGE_SIZE;
  return arr.slice(start, start + PAGE_SIZE);
}

function PageButtons({ total, current, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btns = [];
  for (let i = 1; i <= pages; i++) {
    if (pages > 6 && i > 2 && i < pages - 1 && Math.abs(i - current) > 1) {
      if (btns[btns.length - 1] !== '…') btns.push('…');
    } else {
      btns.push(i);
    }
  }
  return (
    <div className="pay-page-btns">
      {current > 1 && (
        <button className="pay-page-btn" onClick={() => onChange(current - 1)}>‹</button>
      )}
      {btns.map((b, idx) =>
        b === '…'
          ? <button key={`e${idx}`} className="pay-page-btn" disabled>…</button>
          : <button key={b} className={`pay-page-btn${current === b ? ' active' : ''}`} onClick={() => onChange(b)}>{b}</button>
      )}
      {current < pages && (
        <button className="pay-page-btn" onClick={() => onChange(current + 1)}>›</button>
      )}
    </div>
  );
}

function Payments(props) {
  const { data: { month } } = props;
  const [month2] = useState(month);
  const [totals, setTotals] = useState({});
  const [professors, setProfessors] = useState([]);
  const [students, setStudents] = useState([]);
  const [amountLeft, setAmountLeft] = useState('$0');
  const [professorLeft, setProfessorLeft] = useState(0);
  const [studentAmountLeft, setStudentAmountLeft] = useState('$0');
  const [studentLeft, setStudentLeft] = useState(0);
  const [studentString, setStudentString] = useState({});

  // search
  const [profSearch, setProfSearch] = useState('');
  const [stuSearch, setStuSearch] = useState('');

  // pagination
  const [profPage, setProfPage] = useState(1);
  const [stuPage, setStuPage] = useState(1);

  // detail panel
  const [detailType, setDetailType] = useState(null); // 'student' | 'professor'
  const [detailRow, setDetailRow] = useState(null);
  const [detailStatus, setDetailStatus] = useState('');
  const [detailInvoice, setDetailInvoice] = useState('');
  const [detailError, setDetailError] = useState('');

  const professorsPath = `/payments/professors?month=${month}`;
  const studentsPath = `/payments/students?month=${month}`;
  const totalsPath = `/payments/totals?month=${month}`;
  const changeStudentStatusPath = '/payments/students/post';
  const changeProfessorStatusPath = '/payments/professors/post';

  function getStudentsCharges() {
    fetch(studentsPath)
      .then((r) => r.json())
      .then((data) => {
        setStudents(data.amountList);
        setStudentAmountLeft(data.amountLeft);
        setStudentLeft(data.studentLeft);
        setStudentString(data.studentString);
      })
      .catch(console.log);
  }

  function getProfessorsPays() {
    fetch(professorsPath)
      .then((r) => r.json())
      .then((data) => {
        setProfessors(data.amountList);
        setAmountLeft(data.amountLeft);
        setProfessorLeft(data.professorLeft);
      })
      .catch(console.log);
  }

  useEffect(() => {
    fetch(totalsPath)
      .then((r) => r.json())
      .then((data) => setTotals(data))
      .then(() => { getProfessorsPays(); getStudentsCharges(); })
      .catch(console.log);
  }, []);

  // ── Inline checkbox handlers ──────────────────────────────
  function professorStatusChange(e, row) {
    e.stopPropagation();
    const checked = e.target.checked;
    fetch(changeProfessorStatusPath, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ProfessorId: row.id, month: month2, status: checked ? 'OK' : '' }),
    })
      .then((res) => {
        if (res.status === 200) {
          setProfessors((prev) => prev.map((p) => {
            if (p.id !== row.id) return p;
            const newPay = p.pays.length > 0 ? { ...p.pays[0], status: checked ? 'OK' : '' } : { status: checked ? 'OK' : '' };
            return { ...p, pays: [newPay] };
          }));
        }
      })
      .catch(() => alert('Hubo un problema al registrar, por favor, intenta denuevo'));
  }

  function studentStatusChange(e, row) {
    e.stopPropagation();
    const checked = e.target.checked;
    fetch(changeStudentStatusPath, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        StudentId: row.id, month: month2, status: checked ? 'OK' : '',
        invoice: row.charge[0] ? row.charge[0].invoice : '',
      }),
    })
      .then((res) => {
        if (res.status === 200) {
          setStudents((prev) => prev.map((s) => {
            if (s.id !== row.id) return s;
            const newCharge = s.charge.length > 0 ? { ...s.charge[0], status: checked ? 'OK' : '' } : { status: checked ? 'OK' : '' };
            return { ...s, charge: [newCharge] };
          }));
        }
      })
      .catch(() => alert('Hubo un problema al registrar, por favor, intenta denuevo'));
  }

  // ── Detail panel ──────────────────────────────────────────
  function openStudentDetail(row) {
    setDetailType('student');
    setDetailRow(row);
    setDetailStatus(row.charge[0]?.status || '');
    setDetailInvoice(row.charge[0]?.invoice || '');
    setDetailError('');
  }

  function openProfessorDetail(row) {
    setDetailType('professor');
    setDetailRow(row);
    setDetailStatus(row.pays[0]?.status || '');
    setDetailError('');
  }

  function closeDetail() {
    setDetailType(null);
    setDetailRow(null);
  }

  function handleDetailSubmit() {
    if (detailType === 'student') {
      if (detailStatus === '' && detailInvoice === '') {
        setDetailError('Tienes que seleccionar los dos campos');
        return;
      }
      fetch(changeStudentStatusPath, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ StudentId: detailRow.id, month: month2, status: detailStatus, invoice: detailInvoice }),
      })
        .then((res) => { if (res.status === 200) window.location.href = `/payments?month=${month2}`; })
        .catch(() => alert('Hubo un problema al registrar, por favor, intenta denuevo'));
    } else {
      if (!detailStatus && detailStatus !== 'OK') {
        setDetailError('Tienes que seleccionar el campo');
        return;
      }
      fetch(changeProfessorStatusPath, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ ProfessorId: detailRow.id, month: month2, status: detailStatus }),
      })
        .then((res) => { if (res.status === 200) window.location.href = `/payments?month=${month2}`; })
        .catch(() => alert('Hubo un problema al registrar, por favor, intenta denuevo'));
    }
  }

  // ── Nav helpers ───────────────────────────────────────────
  function handleMonthChange(e) { window.location.href = `/payments?month=${e.target.value}`; }
  function downloadPDF() { window.location.href = `/payments/pdf/${detailRow.id}?month=${month}`; }
  function goComissions() { window.location.href = `/payments/comissions?month=${month}`; }
  function goPDFs() { window.location.href = `/payments/pdfs?month=${month}`; }
  function studentsLefts() {
    const result = Object.entries(studentString).map(([k, v]) => `${k}: ${v}`).join(' |---|');
    navigator.clipboard.writeText(JSON.stringify(result)).then(() => alert('Copiado al portapapeles'));
  }
  function downloadPendingStudentsExcel() {
    const pending = students.filter((s) => !s.charge[0] || s.charge[0].status === '');
    const data = pending.map((s) => ({ 'Nombre Alumno': s.name, Apoderado: s.mother, Monto: s.totalAmount, 'Emb.': s.admin.shortName }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pendientes');
    XLSX.writeFile(wb, `${month2}.xlsx`);
  }

  // ── Filtered & sorted lists ───────────────────────────────
  const sortedProfessors = [...professors].sort((a, b) => a.name.localeCompare(b.name));
  const filteredProfessors = sortedProfessors.filter((p) => {
    if (!profSearch.trim()) return true;
    const q = profSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.admin?.shortName || '').toLowerCase().includes(q);
  });

  const sortedStudents = [...students].sort((a, b) => (a.admin?.shortName > b.admin?.shortName ? 1 : -1));
  const filteredStudents = sortedStudents.filter((s) => {
    if (!stuSearch.trim()) return true;
    const q = stuSearch.toLowerCase();
    return (s.name || '').toLowerCase().includes(q)
      || (s.mother || '').toLowerCase().includes(q)
      || (s.totalAmount || '').toString().toLowerCase().includes(q);
  });

  const profSlice = paginate(filteredProfessors, profPage);
  const stuSlice = paginate(filteredStudents, stuPage);

  return (
    <div className="pay-page">

      {/* Header */}
      <div className="pay-header">
        <div className="pay-header-left">
          <h1>Pagos</h1>
          <p>Gestión de cobros del mes</p>
        </div>
        <a className="pay-back" href="/dashboard">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Dashboard
        </a>
      </div>

      {/* Month selector */}
      <div className="pay-month-row">
        <span className="pay-month-label">Mes</span>
        <select className="pay-month-select" value={month2} onChange={handleMonthChange}>
          <option value="">Seleccione el mes</option>
          <option value="Marzo">Marzo</option>
          <option value="Abril">Abril</option>
          <option value="Mayo">Mayo</option>
          <option value="Junio">Junio</option>
          <option value="Julio">Julio</option>
          <option value="Agosto">Agosto</option>
          <option value="Septiembre">Septiembre</option>
          <option value="Octubre">Octubre</option>
          <option value="Noviembre">Noviembre</option>
          <option value="Diciembre">Diciembre</option>
        </select>
      </div>

      {/* Stats */}
      <div className="pay-stats">
        <div className="pay-stat">
          <div className="pay-stat-value">{totals.totalLessons ?? '—'}</div>
          <div className="pay-stat-label">Clases totales</div>
          <div className="pay-stat-sub">{totals.hours ?? '—'} horas</div>
        </div>
        <div className="pay-stat">
          <div className="pay-stat-value">{totals.total ?? '—'}</div>
          <div className="pay-stat-label">Total del mes</div>
        </div>
        <div className="pay-stat">
          <div className={`pay-stat-value${professorLeft > 0 ? ' red' : ''}`}>{amountLeft}</div>
          <div className="pay-stat-label">Pendiente profesores</div>
          <div className="pay-stat-sub">{professorLeft} sin pagar</div>
        </div>
        <div className="pay-stat">
          <div className={`pay-stat-value${studentLeft > 0 ? ' red' : ''}`}>{studentAmountLeft}</div>
          <div className="pay-stat-label">Por recaudar</div>
          <div className="pay-stat-sub">{studentLeft} apoderados</div>
        </div>
      </div>

      {/* Actions */}
      <div className="pay-actions">
        <button className="pay-btn pay-btn-orange" onClick={goPDFs}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Descarga PDFs
        </button>
        <button className="pay-btn pay-btn-outline" onClick={goComissions}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          Ver comisiones
        </button>
        <button className="pay-btn pay-btn-outline" onClick={studentsLefts}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          Apoderados faltantes
        </button>
        <button className="pay-btn pay-btn-green" onClick={downloadPendingStudentsExcel}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          Excel pendientes
        </button>
      </div>

      {/* Two columns */}
      <div className="pay-cols">

        {/* Professors panel */}
        <div className="pay-panel">
          <div className="pay-panel-head">
            <span className="pay-panel-title">Pagos a profesores</span>
            {professorLeft > 0
              ? <span className="pay-panel-badge red">{professorLeft} pendientes</span>
              : <span className="pay-panel-badge">Todo al día</span>}
          </div>
          <div className="pay-search-wrap">
            <input
              className="pay-search"
              placeholder="Buscar profesor…"
              value={profSearch}
              onChange={(e) => { setProfSearch(e.target.value); setProfPage(1); }}
            />
          </div>
          <table className="pay-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Emb.</th>
              </tr>
            </thead>
            <tbody>
              {profSlice.map((prof) => (
                <tr
                  key={prof.id}
                  className={detailType === 'professor' && detailRow?.id === prof.id ? 'pay-row-selected' : ''}
                  onClick={() => openProfessorDetail(prof)}
                >
                  <td>{prof.name}</td>
                  <td className="pay-amount">{prof.totalAmount}</td>
                  <td>
                    <input
                      type="checkbox"
                      className="pay-check"
                      checked={prof.pays[0]?.status === 'OK'}
                      onChange={(e) => professorStatusChange(e, prof)}
                    />
                  </td>
                  <td><span className="pay-emb">{prof.admin?.shortName}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pay-pagination">
            <span className="pay-page-info">
              {filteredProfessors.length === 0 ? '0 resultados' : `${(profPage - 1) * PAGE_SIZE + 1}–${Math.min(profPage * PAGE_SIZE, filteredProfessors.length)} de ${filteredProfessors.length}`}
            </span>
            <PageButtons total={filteredProfessors.length} current={profPage} onChange={setProfPage} />
          </div>
        </div>

        {/* Students panel */}
        <div className="pay-panel">
          <div className="pay-panel-head">
            <span className="pay-panel-title">Cobros apoderados</span>
            {studentLeft > 0
              ? <span className="pay-panel-badge red">{studentLeft} pendientes</span>
              : <span className="pay-panel-badge">Todo al día</span>}
          </div>
          <div className="pay-search-wrap">
            <input
              className="pay-search"
              placeholder="Buscar alumno, apoderado o monto…"
              value={stuSearch}
              onChange={(e) => { setStuSearch(e.target.value); setStuPage(1); }}
            />
          </div>
          <table className="pay-table">
            <thead>
              <tr>
                <th>Alumno/a</th>
                <th>Apoderado/a</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Boleta</th>
                <th>Emb.</th>
              </tr>
            </thead>
            <tbody>
              {stuSlice.map((stu) => (
                <tr
                  key={stu.id}
                  className={detailType === 'student' && detailRow?.id === stu.id ? 'pay-row-selected' : ''}
                  onClick={() => openStudentDetail(stu)}
                >
                  <td>{stu.name}</td>
                  <td>{stu.mother}</td>
                  <td className="pay-amount">{stu.totalAmount}</td>
                  <td>
                    <input
                      type="checkbox"
                      className="pay-check"
                      checked={stu.charge[0]?.status === 'OK'}
                      onChange={(e) => studentStatusChange(e, stu)}
                    />
                  </td>
                  <td>
                    {stu.charge[0]?.invoice === 'OK'
                      ? <span className="pay-invoice-ok">Listo</span>
                      : <span className="pay-invoice-pending">Pendiente</span>}
                  </td>
                  <td><span className="pay-emb">{stu.admin?.shortName}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pay-pagination">
            <span className="pay-page-info">
              {filteredStudents.length === 0 ? '0 resultados' : `${(stuPage - 1) * PAGE_SIZE + 1}–${Math.min(stuPage * PAGE_SIZE, filteredStudents.length)} de ${filteredStudents.length}`}
            </span>
            <PageButtons total={filteredStudents.length} current={stuPage} onChange={setStuPage} />
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {detailRow && (
        <div className="pay-detail">
          <div className="pay-detail-head">
            <div>
              <div className="pay-detail-name">
                {detailType === 'student' ? detailRow.name : detailRow.name}
              </div>
              <div className="pay-detail-sub">
                {detailType === 'student'
                  ? `Apoderado/a: ${detailRow.mother} · ${detailRow.totalAmount} · ${month2}`
                  : `Profesor/a · ${detailRow.totalAmount} · ${month2}`}
              </div>
            </div>
            <button className="pay-detail-close" onClick={closeDetail}>✕</button>
          </div>

          <div className="pay-detail-body">
            {/* Status */}
            <div>
              <div className="pay-detail-section-title">Estado del pago</div>
              <div className="pay-radio-group">
                <label className="pay-radio-label">
                  <input type="radio" name="detail-status" value="OK" checked={detailStatus === 'OK'} onChange={() => setDetailStatus('OK')} />
                  Pagado
                </label>
                <label className="pay-radio-label">
                  <input type="radio" name="detail-status" value="" checked={detailStatus === ''} onChange={() => setDetailStatus('')} />
                  Pendiente
                </label>
              </div>
            </div>

            {/* Invoice (students only) */}
            {detailType === 'student' && (
              <div>
                <div className="pay-detail-section-title">Boleta</div>
                <div className="pay-radio-group">
                  <label className="pay-radio-label">
                    <input type="radio" name="detail-invoice" value="OK" checked={detailInvoice === 'OK'} onChange={() => setDetailInvoice('OK')} />
                    Enviada
                  </label>
                  <label className="pay-radio-label">
                    <input type="radio" name="detail-invoice" value="" checked={detailInvoice === ''} onChange={() => setDetailInvoice('')} />
                    Pendiente
                  </label>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <button className="pay-btn pay-btn-outline" style={{ fontSize: '11px', padding: '6px 12px' }} onClick={downloadPDF}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Descargar boleta
                  </button>
                </div>
              </div>
            )}
          </div>

          {detailError && <div className="pay-detail-error">{detailError}</div>}

          <div className="pay-detail-actions">
            <button className="pay-btn pay-btn-outline" onClick={closeDetail}>Cancelar</button>
            <button className="pay-btn pay-btn-orange" onClick={handleDetailSubmit}>Registrar</button>
          </div>
        </div>
      )}

    </div>
  );
}

export default hot(module)(Payments);
